/**
 * SubscriptionAndStorageTab — Phase 3 (Drive-First Pivot).
 *
 * Unified "your plan + your storage" page that combines the previously
 * separate Subscription and Cloud Storage tabs into a single coherent view.
 * Status hero at the top adapts to current state (trial / active / past-due
 * / blocked / no-drive), followed by two side-by-side cards (subscription
 * & storage), then a combined activity log + GDPR footer.
 *
 * Designed to be:
 *   - Compact (text-xs / text-sm throughout)
 *   - Professional (semantic color palette, subtle shadows, no gradients)
 *   - Responsive (single column on mobile, two-column on md+)
 *   - Self-explanatory (no jargon, clear status pills, plain-language tips)
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineCreditCard,
  HiOutlineCloud,
  HiOutlineArrowPath,
  HiOutlineExclamationTriangle,
  HiOutlineSparkles,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineEnvelope,
  HiOutlineUser,
  HiOutlineCalendar,
  HiOutlineCurrencyDollar,
  HiOutlineShieldCheck,
} from 'react-icons/hi2'
import { usePricingMode } from '../../contexts/PricingModeContext'
import { useSubscription } from '../../hooks/useSubscription'
import { subscriptionAPI } from '../../services/subscription'
import { googleDriveAPI } from '../../services/integrations/googleDrive'
import { useToast } from '../../components/common/ToastContainer'
import RelinkDriveFilesButton from '../../components/documents/RelinkDriveFilesButton'

export default function SubscriptionAndStorageTab({ isOwner = true }) {
  const { isDriveFirst, config } = usePricingMode()
  const { subscription, loading: subLoading, reload: reloadSub } = useSubscription()
  const toast = useToast()

  const [driveStatus, setDriveStatus] = useState(null)
  const [driveLoading, setDriveLoading] = useState(true)
  const [events, setEvents] = useState([])
  const [actionInFlight, setActionInFlight] = useState(null)
  const [disconnectInput, setDisconnectInput] = useState('')
  const [showDisconnect, setShowDisconnect] = useState(false)

  const loadDrive = useCallback(async () => {
    try {
      const res = await googleDriveAPI.getStatus()
      const payload = res?.data?.data ?? res?.data ?? null
      setDriveStatus(payload)
    } catch (e) {
      setDriveStatus(null)
    } finally {
      setDriveLoading(false)
    }
  }, [])

  const loadEvents = useCallback(async () => {
    try {
      const res = await subscriptionAPI.events()
      const payload = res?.data?.data ?? res?.data ?? []
      setEvents(Array.isArray(payload) ? payload : [])
    } catch (e) { /* non-fatal */ }
  }, [])

  useEffect(() => {
    loadDrive()
    loadEvents()
  }, [loadDrive, loadEvents])

  // ─── Action handlers ─────────────────────────────────────────────────

  const handleCheckout = async () => {
    setActionInFlight('checkout')
    try {
      const res = await subscriptionAPI.checkout()
      const url = res?.data?.data?.checkout_url ?? res?.data?.checkout_url
      if (url) { window.location.href = url; return }
      toast?.error?.(res?.data?.message || 'Could not start checkout.', 'Subscription')
    } catch (e) {
      toast?.error?.('Could not start checkout.', 'Subscription')
    } finally { setActionInFlight(null) }
  }

  const handleBillingPortal = async () => {
    setActionInFlight('portal')
    try {
      const res = await subscriptionAPI.billingPortal()
      const url = res?.data?.data?.portal_url ?? res?.data?.portal_url
      if (url) { window.location.href = url; return }
      toast?.error?.(res?.data?.message || 'Could not open billing portal.', 'Subscription')
    } catch (e) {
      toast?.error?.('Could not open billing portal.', 'Subscription')
    } finally { setActionInFlight(null) }
  }

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current period? You can reactivate any time before then.')) return
    setActionInFlight('cancel')
    try {
      const res = await subscriptionAPI.cancel({ at_period_end: true, reason: 'user_request' })
      if (res?.success) {
        toast?.success?.('Cancellation scheduled.', 'Subscription')
        reloadSub()
        loadEvents()
      } else {
        toast?.error?.(res?.data?.message || 'Cancel failed.', 'Subscription')
      }
    } catch (e) {
      toast?.error?.('Cancel failed.', 'Subscription')
    } finally { setActionInFlight(null) }
  }

  const handleReactivate = async () => {
    setActionInFlight('reactivate')
    try {
      const res = await subscriptionAPI.reactivate()
      if (res?.success) {
        toast?.success?.('Subscription reactivated.', 'Subscription')
        reloadSub()
        loadEvents()
      } else {
        toast?.error?.(res?.data?.message || 'Reactivation failed.', 'Subscription')
      }
    } catch (e) {
      toast?.error?.('Reactivation failed.', 'Subscription')
    } finally { setActionInFlight(null) }
  }

  const handleConnectDrive = async () => {
    setActionInFlight('connect')
    try {
      const res = await googleDriveAPI.getAuthUrl('web')
      const url = res?.data?.data?.auth_url ?? res?.data?.auth_url
      if (url) { window.location.href = url; return }
      toast?.error?.(res?.data?.message || 'Could not start Drive connection.', 'Drive')
    } catch (e) {
      toast?.error?.('Could not start Drive connection.', 'Drive')
    } finally { setActionInFlight(null) }
  }

  const handleTestDrive = async () => {
    setActionInFlight('test')
    try {
      const res = await googleDriveAPI.test()
      const payload = res?.data?.data ?? res?.data
      if (res?.success && payload?.ok) {
        const latency = payload?.latency_ms
        toast?.success?.(latency != null ? `Connection healthy (${latency} ms).` : 'Connection healthy.', 'Drive')
      } else {
        toast?.error?.(payload?.reason || res?.data?.message || 'Health check failed.', 'Drive')
      }
    } catch (e) {
      toast?.error?.('Health check failed.', 'Drive')
    } finally {
      setActionInFlight(null)
      loadDrive()
    }
  }

  const handleDisconnectConfirm = async () => {
    if (disconnectInput !== 'DISCONNECT') return
    setActionInFlight('disconnect')
    try {
      const res = await googleDriveAPI.disconnect()
      if (res?.success) {
        const stranded = res?.data?.data?.stranded_file_count ?? 0
        toast?.success?.(stranded > 0
          ? `Disconnected. ${stranded} file${stranded === 1 ? '' : 's'} on Drive will be inaccessible until you reconnect.`
          : 'Disconnected.', 'Drive')
      } else {
        toast?.error?.(res?.data?.message || 'Disconnect failed.', 'Drive')
      }
    } catch (e) {
      toast?.error?.('Disconnect failed.', 'Drive')
    } finally {
      setShowDisconnect(false)
      setDisconnectInput('')
      setActionInFlight(null)
      loadDrive()
      reloadSub()
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────

  if (!isOwner) {
    return (
      <div className="rounded-lg border border-warning/30 bg-warning-soft p-4 text-xs text-amber-900">
        Only the workspace owner can manage subscription and storage.
      </div>
    )
  }

  const loading = subLoading || driveLoading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-brand" />
      </div>
    )
  }

  const annualPrice = config?.annual_price_usd || subscription?.current_price_usd || '60'
  const trialDays = config?.trial_days || 90
  const driveConnected = driveStatus?.connected === true
  const driveProvider = driveStatus?.provider

  return (
    <div className="space-y-5">
      {/* ─── Top status hero ────────────────────────────────────────── */}
      <StatusHero
        subscription={subscription}
        driveConnected={driveConnected}
        annualPrice={annualPrice}
        trialDays={trialDays}
        onAction={(action) => {
          if (action === 'subscribe') handleCheckout()
          else if (action === 'connect_drive') handleConnectDrive()
          else if (action === 'update_payment') handleBillingPortal()
          else if (action === 'reactivate') handleReactivate()
        }}
        inFlight={actionInFlight}
      />

      {/* ─── Two-column body: subscription + drive ──────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SubscriptionCard
          subscription={subscription}
          annualPrice={annualPrice}
          onSubscribe={handleCheckout}
          onBillingPortal={handleBillingPortal}
          onCancel={handleCancel}
          onReactivate={handleReactivate}
          inFlight={actionInFlight}
        />
        <StorageCard
          driveStatus={driveStatus}
          subscription={subscription}
          onConnect={handleConnectDrive}
          onTest={handleTestDrive}
          onDisconnect={() => setShowDisconnect(true)}
          inFlight={actionInFlight}
        />
      </div>

      {/* ─── Activity log ──────────────────────────────────────────── */}
      {events.length > 0 && (
        <ActivityCard events={events} />
      )}

      {/* ─── Footer note ────────────────────────────────────────────── */}
      <div className="text-[11px] text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-lg p-3">
        <div className="flex items-start gap-1.5">
          <HiOutlineShieldCheck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
          <span>
            Files you upload after connecting Drive are stored in your own Google Drive account —
            OVACS never holds them. You can disconnect any time; your Drive files are kept.
          </span>
        </div>
      </div>

      {/* ─── Disconnect modal ──────────────────────────────────────── */}
      {showDisconnect && (
        <DisconnectModal
          driveStatus={driveStatus}
          input={disconnectInput}
          setInput={setDisconnectInput}
          onCancel={() => { setShowDisconnect(false); setDisconnectInput('') }}
          onConfirm={handleDisconnectConfirm}
          inFlight={actionInFlight === 'disconnect'}
        />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Status hero — adapts to current state
// ═══════════════════════════════════════════════════════════════════════

const StatusHero = ({ subscription, driveConnected, annualPrice, trialDays, onAction, inFlight }) => {
  const sub = subscription

  // No subscription + no Drive → "Get started"
  if (!sub) {
    return (
      <Hero tone="indigo" icon={HiOutlineSparkles}
        title="Connect your Drive to start"
        body={`${trialDays} days completely free. After that, $${annualPrice}/year. No credit card needed during the trial.`}
        cta={driveConnected ? null : { label: 'Connect Google Drive', action: 'connect_drive' }}
        onAction={onAction} inFlight={inFlight === 'connect'} />
    )
  }

  if (sub.is_grandfathered) {
    return (
      <Hero tone="indigo" icon={HiOutlineCheckCircle}
        title="Legacy plan — no subscription required"
        body="You're on a grandfathered plan. Your existing files stay on OVACS storage, no payment required."
        cta={null} onAction={onAction} />
    )
  }

  if (sub.is_in_trial) {
    const d = sub.days_until_trial_end ?? 0
    const tone = d <= 1 ? 'red' : d <= 7 ? 'amber' : 'blue'
    const urgency = d <= 1 ? 'Last day!' : d <= 7 ? `Only ${d} days left` : `${d} days left`
    return (
      <Hero tone={tone} icon={HiOutlineSparkles}
        title={`Free trial — ${urgency}`}
        body={`Your trial ends on ${sub.trial_ends_at ? new Date(sub.trial_ends_at).toLocaleDateString() : 'soon'}. Subscribe to continue uploading after that.`}
        progressLabel={`${d} of ${trialDays} days remaining`}
        progressPercent={Math.max(0, Math.min(100, (d / trialDays) * 100))}
        cta={{ label: `Subscribe — $${annualPrice}/year`, action: 'subscribe' }}
        onAction={onAction} inFlight={inFlight === 'checkout'} />
    )
  }

  if (sub.is_active_paid) {
    return (
      <Hero tone="green" icon={HiOutlineCheckCircle}
        title="Active subscription"
        body={sub.cancelled_at
          ? `Cancellation scheduled — access ends ${sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'soon'}.`
          : `Renews automatically on ${sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—'}.`}
        cta={sub.cancelled_at ? { label: 'Reactivate', action: 'reactivate' } : null}
        onAction={onAction} inFlight={inFlight === 'reactivate'} />
    )
  }

  if (sub.is_in_grace) {
    const endsAt = sub.grace_period_ends_at ? new Date(sub.grace_period_ends_at) : null
    const daysLeft = endsAt ? Math.max(0, Math.ceil((endsAt - Date.now()) / 86400000)) : null
    return (
      <Hero tone="amber" icon={HiOutlineExclamationTriangle}
        title="Payment failed — update card to keep uploading"
        body={daysLeft != null
          ? `${daysLeft} days remaining before access is paused. Update your payment method to avoid interruption.`
          : 'Update your payment method to keep your subscription active.'}
        cta={{ label: 'Update payment method', action: 'update_payment' }}
        onAction={onAction} inFlight={inFlight === 'portal'} />
    )
  }

  if (sub.is_blocked || sub.status === 'expired') {
    return (
      <Hero tone="red" icon={HiOutlineXCircle}
        title={sub.status === 'expired' ? 'Your trial has ended' : 'Subscription needs renewal'}
        body="Uploads are paused. Your data is safe — view and download still work. Subscribe to resume."
        cta={{ label: `Subscribe — $${annualPrice}/year`, action: 'subscribe' }}
        onAction={onAction} inFlight={inFlight === 'checkout'} />
    )
  }

  if (sub.status === 'cancelled') {
    return (
      <Hero tone="gray" icon={HiOutlineClock}
        title="Subscription cancelled"
        body="You can resubscribe any time — your data is kept."
        cta={{ label: `Resubscribe — $${annualPrice}/year`, action: 'subscribe' }}
        onAction={onAction} inFlight={inFlight === 'checkout'} />
    )
  }

  // Fallback
  return (
    <Hero tone="gray" icon={HiOutlineClock}
      title="Subscription status"
      body={`Current status: ${sub.status}`}
      cta={null} onAction={onAction} />
  )
}

const TONE_STYLES = {
  blue:   { bg: 'bg-info-soft',    border: 'border-info/30',    iconBg: 'bg-blue-100',    iconColor: 'text-brand',    titleColor: 'text-blue-900',    button: 'bg-brand hover:bg-brand-hover',    progress: 'bg-info-soft0' },
  green:  { bg: 'bg-success-soft',   border: 'border-success/30',   iconBg: 'bg-green-100',   iconColor: 'text-success',   titleColor: 'text-green-900',   button: 'bg-green-600 hover:bg-green-700',   progress: 'bg-success-soft0' },
  amber:  { bg: 'bg-warning-soft',   border: 'border-warning/30',   iconBg: 'bg-amber-100',   iconColor: 'text-warning',   titleColor: 'text-amber-900',   button: 'bg-amber-600 hover:bg-amber-700',   progress: 'bg-warning-soft0' },
  red:    { bg: 'bg-danger-soft',     border: 'border-danger/30',     iconBg: 'bg-red-100',     iconColor: 'text-danger',     titleColor: 'text-red-900',     button: 'bg-red-600 hover:bg-red-700',     progress: 'bg-danger-soft0' },
  indigo: { bg: 'bg-indigo-50',  border: 'border-indigo-200',  iconBg: 'bg-indigo-100',  iconColor: 'text-indigo-700',  titleColor: 'text-indigo-900',  button: 'bg-indigo-600 hover:bg-indigo-700', progress: 'bg-indigo-500' },
  gray:   { bg: 'bg-gray-50',    border: 'border-gray-200',    iconBg: 'bg-gray-100',    iconColor: 'text-gray-700',    titleColor: 'text-gray-900',    button: 'bg-gray-700 hover:bg-gray-800',     progress: 'bg-gray-500' },
}

const Hero = ({ tone = 'blue', icon: Icon, title, body, progressLabel, progressPercent, cta, onAction, inFlight }) => {
  const s = TONE_STYLES[tone] || TONE_STYLES.blue
  return (
    <div className={`rounded-xl border ${s.bg} ${s.border} p-4 sm:p-5`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`w-10 h-10 rounded-lg ${s.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${s.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className={`text-sm sm:text-base font-semibold ${s.titleColor}`}>{title}</h2>
          {body && <p className="text-xs sm:text-[13px] text-gray-700 mt-1 leading-relaxed">{body}</p>}
          {progressPercent != null && (
            <div className="mt-3">
              <div className="w-full bg-white/60 rounded-full h-1.5">
                <div className={`${s.progress} h-1.5 rounded-full transition-all`} style={{ width: `${progressPercent}%` }} />
              </div>
              {progressLabel && <p className="text-[11px] text-gray-600 mt-1">{progressLabel}</p>}
            </div>
          )}
        </div>
        {cta && (
          <button
            type="button"
            onClick={() => onAction(cta.action)}
            disabled={!!inFlight}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-white rounded-lg disabled:opacity-60 ${s.button} flex-shrink-0`}
          >
            {inFlight ? 'Working…' : cta.label}
          </button>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Subscription card
// ═══════════════════════════════════════════════════════════════════════

const SubscriptionCard = ({ subscription, annualPrice, onSubscribe, onBillingPortal, onCancel, onReactivate, inFlight }) => {
  const sub = subscription
  const planName = sub?.plan?.name || 'OVACS Annual'

  return (
    <Card title="Your subscription" icon={HiOutlineCreditCard}>
      <div className="space-y-3">
        {/* Plan summary */}
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-gray-900">{planName}</div>
            {sub && <StatusPill status={sub.status} />}
          </div>
          <div className="text-right">
            <div className="text-lg sm:text-xl font-bold text-gray-900">${sub?.current_price_usd || annualPrice}</div>
            <div className="text-[11px] text-gray-500">per year</div>
          </div>
        </div>

        {/* Key dates */}
        {sub && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            {sub.is_in_trial && sub.trial_ends_at && (
              <Field label="Trial ends" value={new Date(sub.trial_ends_at).toLocaleDateString()} />
            )}
            {sub.is_active_paid && sub.current_period_end && (
              <Field
                label={sub.cancelled_at ? 'Access ends' : 'Renews on'}
                value={new Date(sub.current_period_end).toLocaleDateString()}
              />
            )}
            {sub.is_in_grace && sub.grace_period_ends_at && (
              <Field label="Grace ends" value={new Date(sub.grace_period_ends_at).toLocaleDateString()} highlight="amber" />
            )}
            {sub.current_period_start && sub.is_active_paid && (
              <Field label="Started" value={new Date(sub.current_period_start).toLocaleDateString()} />
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-1.5 pt-1">
          {sub?.is_in_trial && (
            <Btn primary onClick={onSubscribe} disabled={inFlight === 'checkout'}>
              {inFlight === 'checkout' ? 'Opening…' : 'Subscribe now'}
            </Btn>
          )}
          {sub?.is_active_paid && !sub.cancelled_at && (
            <>
              <Btn onClick={onBillingPortal} disabled={inFlight === 'portal'}>
                {inFlight === 'portal' ? 'Opening…' : 'Manage billing'}
              </Btn>
              <Btn danger onClick={onCancel} disabled={inFlight === 'cancel'}>
                {inFlight === 'cancel' ? 'Cancelling…' : 'Cancel'}
              </Btn>
            </>
          )}
          {sub?.cancelled_at && sub.is_active_paid && (
            <Btn primary onClick={onReactivate} disabled={inFlight === 'reactivate'}>
              {inFlight === 'reactivate' ? 'Reactivating…' : 'Reactivate'}
            </Btn>
          )}
          {sub?.is_in_grace && (
            <Btn primary onClick={onBillingPortal} disabled={inFlight === 'portal'}>
              {inFlight === 'portal' ? 'Opening…' : 'Update payment'}
            </Btn>
          )}
          {(sub?.is_blocked || sub?.status === 'expired' || sub?.status === 'cancelled') && (
            <Btn primary onClick={onSubscribe} disabled={inFlight === 'checkout'}>
              {inFlight === 'checkout' ? 'Opening…' : 'Subscribe'}
            </Btn>
          )}
          {!sub && (
            <p className="text-xs text-gray-500">No subscription yet. Connect Drive to start your free trial.</p>
          )}
        </div>
      </div>
    </Card>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Storage card
// ═══════════════════════════════════════════════════════════════════════

// ─── Re-link files stored by a previous Google connection ───────────────────
// Rendered inside the Storage card when connected. Probes Drive once per mount
// (batched server-side) and, when some files can't be opened by the current
// app — e.g. the Google Cloud project behind OVACS was recreated — offers the
// Picker-based re-link. The files are untouched in Drive; only the grant is
// gone, and picking them in the Picker restores it. Nothing is copied or moved.
const RelinkSection = ({ driveStatus }) => {
  const [checking, setChecking] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const connected = driveStatus?.connected === true

  const check = useCallback(async () => {
    setChecking(true)
    setError(null)
    try {
      const res = await googleDriveAPI.relink.candidates()
      if (res?.success) {
        setResult(res?.data?.data ?? res?.data ?? null)
      } else {
        setError(res?.error?.message || res?.data?.message || 'Could not check your files.')
      }
    } catch {
      setError('Could not check your files.')
    } finally {
      setChecking(false)
    }
  }, [])

  useEffect(() => {
    if (connected) check()
  }, [connected, check])

  if (!connected) return null
  const inaccessible = result?.inaccessible || []
  if (!error && (!result || inaccessible.length === 0)) return null

  const parent = result?.previous_root_folder_id || driveStatus?.previous_root_folder_id || null
  const shown = inaccessible.slice(0, 5)

  return (
    <div className="p-2.5 bg-warning-soft border border-warning/30 rounded text-[11px] text-amber-900 space-y-2">
      {error ? (
        <div className="flex items-center justify-between gap-2">
          <span>{error}</span>
          <Btn onClick={check} disabled={checking}>{checking ? 'Checking…' : 'Try again'}</Btn>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2">
            <HiOutlineExclamationTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="space-y-1 min-w-0">
              <p className="font-semibold">
                {inaccessible.length} of {result.checked} file{result.checked === 1 ? '' : 's'} can&apos;t be opened right now
              </p>
              <p className="leading-relaxed">
                These files were stored by a previous OVACS connection and are still safe in your
                Google Drive — OVACS just lost its permission to read them. Click{' '}
                <strong>Re-link files</strong>, select the files in your old OVACS folder (selecting
                all of them at once is fine), and OVACS will reconnect them. Nothing is copied or moved.
              </p>
              <ul className="list-disc ms-4 text-amber-800">
                {shown.map((d) => (
                  <li key={d.id} className="truncate">{d.title || d.file_name || `Document #${d.id}`}</li>
                ))}
                {inaccessible.length > shown.length && (
                  <li className="list-none text-amber-700">+{inaccessible.length - shown.length} more</li>
                )}
              </ul>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <RelinkDriveFilesButton parentFolderId={parent} onRelinked={check} />
            <Btn onClick={check} disabled={checking}>{checking ? 'Checking…' : 'Check again'}</Btn>
          </div>
        </>
      )}
    </div>
  )
}

const StorageCard = ({ driveStatus, subscription, onConnect, onTest, onDisconnect, inFlight }) => {
  const connected = driveStatus?.connected === true
  const quota = driveStatus?.quota
  const usagePct = quota ? Math.min(100, quota.percent || 0) : 0

  return (
    <Card title="Storage" icon={HiOutlineCloud}>
      {!connected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-gray-700">
            <span className="w-2 h-2 rounded-full bg-gray-400" />
            <span>Google Drive not connected</span>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Connect your Google Drive to store files in your own cloud account.
            We only access the folder OVACS creates — never the rest of your Drive.
          </p>
          <Btn primary onClick={onConnect} disabled={inFlight === 'connect'}>
            <HiOutlineCloud className="w-3.5 h-3.5" />
            {inFlight === 'connect' ? 'Connecting…' : 'Connect Google Drive'}
          </Btn>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full bg-success-soft0" />
            <span className="text-green-800 font-medium">Connected to Google Drive</span>
          </div>

          {/* Account info */}
          <div className="grid grid-cols-1 gap-2 text-xs">
            {driveStatus?.google_account_email && (
              <Field
                icon={HiOutlineEnvelope}
                label="Account"
                value={driveStatus.google_account_email}
              />
            )}
            {driveStatus?.google_account_name && (
              <Field
                icon={HiOutlineUser}
                label="Name"
                value={driveStatus.google_account_name}
              />
            )}
            {driveStatus?.connected_at && (
              <Field
                icon={HiOutlineCalendar}
                label="Connected on"
                value={new Date(driveStatus.connected_at).toLocaleDateString()}
              />
            )}
          </div>

          {/* OVACS footprint — what OUR files occupy, independent of the rest
              of the user's Drive. Comes from our own DB aggregate, which under
              the drive.file scope equals exactly the OVACS folder contents. */}
          {typeof driveStatus?.provider_file_bytes === 'number' && (
            <div className="flex items-center justify-between text-[11px] pb-1 border-b border-gray-100">
              <span className="text-gray-600">OVACS files in Drive</span>
              <span className="font-medium text-gray-900">
                {formatBytes(driveStatus.provider_file_bytes)}
                {driveStatus.provider_file_count != null && (
                  <span className="text-gray-500 font-normal">
                    {' '}({driveStatus.provider_file_count} file{driveStatus.provider_file_count === 1 ? '' : 's'})
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Usage bar — the WHOLE Google Drive account (all 15 GB), because
              OVACS files are owned by the user and count against this total, so
              it predicts when uploads will start failing. */}
          {quota && (
            <div>
              <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1">
                <span>Your Google Drive (all files)</span>
                <span className="font-medium text-gray-900">
                  {formatBytes(quota.used)} of {formatBytes(quota.total)} ({usagePct}%)
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    usagePct >= 95 ? 'bg-danger-soft0' : usagePct >= 85 ? 'bg-warning-soft0' : 'bg-cyan-500'
                  }`}
                  style={{ width: `${usagePct}%` }}
                />
              </div>
              {usagePct >= 85 && (
                <p className={`text-[11px] mt-1.5 ${usagePct >= 95 ? 'text-danger' : 'text-warning'}`}>
                  {usagePct >= 95
                    ? 'Drive nearly full — uploads will fail soon. Free up space or upgrade your Google plan.'
                    : 'Drive is filling up — consider freeing space or upgrading your Google plan.'}
                </p>
              )}
            </div>
          )}

          {/* Folder link */}
          {driveStatus?.folder_url && (
            <a
              href={driveStatus.folder_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900 font-medium"
            >
              <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5" />
              Open OVACS folder in Drive
            </a>
          )}

          {/* Files stored by a previous Google connection that this app can't open */}
          <RelinkSection driveStatus={driveStatus} />

          {/* Actions */}
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Btn onClick={onTest} disabled={inFlight === 'test'}>
              {inFlight === 'test' ? 'Testing…' : 'Test connection'}
            </Btn>
            <Btn danger onClick={onDisconnect} disabled={!!inFlight}>
              Disconnect
            </Btn>
          </div>
        </div>
      )}

      {driveStatus?.last_error && (
        <div className="mt-3 p-2 bg-warning-soft border border-warning/30 rounded text-[11px] text-amber-900">
          <strong>Last error:</strong> {driveStatus.last_error}
        </div>
      )}
    </Card>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Activity log
// ═══════════════════════════════════════════════════════════════════════

const EVENT_LABELS = {
  trial_started: 'Free trial started',
  trial_ending_soon: 'Trial-ending reminder',
  trial_ended: 'Trial ended',
  subscribed: 'Subscribed',
  renewed: 'Subscription renewed',
  payment_failed: 'Payment failed',
  grace_started: 'Grace period started',
  blocked: 'Subscription blocked',
  unblocked: 'Subscription restored',
  cancelled: 'Cancelled',
  admin_override_applied: 'Admin action',
  grandfathered: 'Legacy plan assigned',
  drive_connected: 'Drive connected',
  drive_disconnected: 'Drive disconnected',
  price_changed: 'Renewal price changed',
  refunded: 'Refund issued',
}

const ActivityCard = ({ events }) => (
  <Card title="Recent activity" icon={HiOutlineClock} compact>
    <div className="divide-y divide-gray-100 -mx-3 sm:-mx-4">
      {events.slice(0, 8).map((e) => (
        <div key={e.id} className="px-3 sm:px-4 py-1.5 flex items-center justify-between text-xs">
          <span className="text-gray-700">{EVENT_LABELS[e.event_type] || e.event_type}</span>
          <span className="text-[11px] text-gray-400">{formatRelative(e.created_at)}</span>
        </div>
      ))}
    </div>
  </Card>
)

// ═══════════════════════════════════════════════════════════════════════
// Disconnect modal
// ═══════════════════════════════════════════════════════════════════════

const DisconnectModal = ({ driveStatus, input, setInput, onCancel, onConfirm, inFlight }) => {
  const fileCount = driveStatus?.provider_file_count || 0
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
        <h3 className="text-base font-semibold text-gray-900">Disconnect Google Drive?</h3>
        <div className="text-xs text-gray-700 space-y-1.5">
          <p>✓ Your files in Drive are <strong>not deleted</strong> — they stay in the <code className="bg-gray-100 px-1 rounded">OVACS/</code> folder.</p>
          <p>✓ New uploads will go back to OVACS storage (subject to your subscription).</p>
          {fileCount > 0 && (
            <p className="text-warning">
              ⚠ <strong>{fileCount} file{fileCount === 1 ? '' : 's'}</strong> stored on Drive will be inaccessible from OVACS until you reconnect.
            </p>
          )}
        </div>
        <div>
          <label className="text-xs text-gray-700 block mb-1">
            Type <span className="font-mono font-semibold">DISCONNECT</span> to confirm:
          </label>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono"
            autoFocus
            placeholder="DISCONNECT"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Btn onClick={onCancel} disabled={inFlight}>Cancel</Btn>
          <button
            type="button"
            onClick={onConfirm}
            disabled={input !== 'DISCONNECT' || inFlight}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {inFlight ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Small reusable atoms
// ═══════════════════════════════════════════════════════════════════════

const Card = ({ title, icon: Icon, children, compact = false }) => (
  <div className={`bg-white rounded-xl border border-gray-200 ${compact ? 'p-3 sm:p-4' : 'p-4 sm:p-5'}`}>
    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 flex items-center gap-1.5 mb-3">
      {Icon && <Icon className="w-4 h-4 text-gray-500" />}
      {title}
    </h3>
    {children}
  </div>
)

const Field = ({ icon: Icon, label, value, highlight }) => {
  const tone = highlight === 'amber' ? 'text-warning font-semibold' : 'text-gray-900'
  return (
    <div className="flex items-start gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase text-gray-500 tracking-wide">{label}</div>
        <div className={`text-xs ${tone} break-all`}>{value || '—'}</div>
      </div>
    </div>
  )
}

const Btn = ({ primary, danger, children, onClick, disabled }) => {
  const cls = primary
    ? 'bg-brand hover:bg-brand-hover text-white border-brand'
    : danger
      ? 'bg-white text-danger border-red-300 hover:bg-danger-soft'
      : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border ${cls} disabled:opacity-60`}
    >
      {children}
    </button>
  )
}

const STATUS_PILL_STYLES = {
  trial:         { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Free trial' },
  active:        { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Active' },
  past_due:      { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Payment due' },
  blocked:       { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Blocked' },
  cancelled:     { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Cancelled' },
  expired:       { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Expired' },
  grandfathered: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Legacy plan' },
  pending:       { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Pending' },
}

const StatusPill = ({ status }) => {
  const s = STATUS_PILL_STYLES[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status }
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

function formatBytes(bytes) {
  if (bytes == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`
}

function formatRelative(iso) {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  const diff = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return new Date(iso).toLocaleDateString()
}
